import dayjs, { Dayjs } from 'dayjs';

/**
 * 签收时间（= 妥投时间）可选范围。
 *
 * 包裹查询只覆盖妥投后 N 天内的包裹（更早的已归档到历史包裹查询），可选最早时间 = 当前时间 − N 天。
 * 订单时间不做任何限制。days 为 null（配置未加载）时不做限制。
 */
export type DateTimeRangeValue = [string | null, string | null] | null | undefined;

/** 与 DateTimeRangeFilterItem 输出的格式一致 */
export const RANGE_VALUE_FORMAT = 'YYYY-MM-DD HH:mm:ss';

export const getEarliestDeliveredAt = (days: number, now: Dayjs = dayjs()): Dayjs => now.subtract(days, 'day');

const isComplete = (value: DateTimeRangeValue): value is [string, string] =>
    !!value && Array.isArray(value) && !!value[0] && !!value[1];

/** 开始、结束都早于可选最早时间：输入框标红、查询按钮禁用 */
export function isDeliveredAtOutOfRange(value: DateTimeRangeValue, days: number | null, now: Dayjs = dayjs()): boolean {
    if (days == null || !isComplete(value)) {
        return false;
    }
    const earliest = getEarliestDeliveredAt(days, now);
    return dayjs(value[0]).isBefore(earliest) && dayjs(value[1]).isBefore(earliest);
}

export interface NormalizedDeliveredAt {
    value: DateTimeRangeValue;
    /** 开始时间被自动调整时为调整后的值（RANGE_VALUE_FORMAT），用于橙色提示 */
    adjustedStart?: string;
}

/** 仅开始时间早于可选最早时间、结束时间在范围内：开始时间调整为可选最早时间 */
export function normalizeDeliveredAt(value: DateTimeRangeValue, days: number | null, now: Dayjs = dayjs()): NormalizedDeliveredAt {
    if (days == null || !isComplete(value)) {
        return { value };
    }
    const earliest = getEarliestDeliveredAt(days, now);
    if (dayjs(value[0]).isBefore(earliest) && !dayjs(value[1]).isBefore(earliest)) {
        const adjustedStart = earliest.format(RANGE_VALUE_FORMAT);
        return { value: [adjustedStart, value[1]], adjustedStart };
    }
    return { value };
}

/** 日期面板：早于可选最早时间所在日的日期置灰（当天可选，更早的时刻由 normalizeDeliveredAt 调整） */
export const isDateBeforeEarliest = (current: Dayjs, days: number | null, now: Dayjs = dayjs()): boolean =>
    days != null && !!current && current.isBefore(getEarliestDeliveredAt(days, now).startOf('day'));
